import { useSelector } from 'react-redux';
import { selectSession, selectSessionAdvisor, selectMaybeClient } from '../model';
import CompanyLogoContainer from './CompanyLogo';

import { XMarkIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { SessionType } from '../../../shared/src/SessionType';
import { useAdvisorNavigation } from '../hooks/useAdvisorNavigation';
import { useNavigate } from 'react-router-dom';
import { ClientUserMenu } from './ClientUserMenu';
import { ContactAdvisorButton } from './ContactAdvisorButton';
import { RequestAdvisorMeetingModal } from './RequestAdvisorMeetingModal';
import { useClientSignOut } from './hooks/use-sign-out';
import { CustomTooltip } from './CustomTooltip';
import { useNavigateToIntakeForm } from '../hooks/useNavigateToIntakeForm';
import { useClientRoutes } from '../hooks/useClientRoutes';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { ClientStatus } from '@shared';

type TopBannerOnboardingProps = {
  className?: string;
  actionButton?: ReactNode;
};

export function TopBannerOnboarding({ className, actionButton }: TopBannerOnboardingProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const session = useSelector(selectSession);
  const client = useSelector(selectMaybeClient);
  const { auth0Session } = session;
  const { navigateToClientsDashboard } = useAdvisorNavigation();
  const navigate = useNavigate();
  const handleSignOut = useClientSignOut();
  const { isClientOnboardingRoute } = useClientRoutes();
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const navigateToClientHome = () => {
    navigate('/client/home');
  };

  const [isRequestMeetingOpen, setIsRequestMeetingOpen] = useState(false);

  const isClient = session.sessionType === SessionType.client;

  const hasFormReference = !!client?.formReferenceId;
  const isFormCompleted = client?.formReferenceStatus === 'completed';
  const isClientActive = client?.clientStatus === ClientStatus.active;
  // For clients: only show when form is not completed
  // For advisors: show when form reference exists (can update even if completed)
  const showIntakeFormButton =
    isClientOnboardingRoute &&
    useFormProvider &&
    hasFormReference &&
    (isClient ? !isFormCompleted : true);
  const buttonText = isClientActive ? 'Update Intake Form' : 'Complete Intake Form';

  const updateIntakeFormButton = showIntakeFormButton && (
    <CustomTooltip
      content={
        isClient
          ? 'Update your intake form information to get more accurate predictions. Our AI models use your current health status, family history, and care preferences to personalize your long-term care predictions.'
          : "Update the client's intake form information to get more accurate predictions."
      }
      side="bottom"
      align="end"
    >
      <button
        onClick={navigateToIntakeForm}
        disabled={isLoadingForm}
        className={classNames(
          'border-primary text-primary hover:bg-primary-50 focus-visible:outline-primary cursor-pointer rounded-md border bg-white px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50',
          isClient && 'flex w-full items-center justify-center text-center sm:w-auto',
        )}
        data-testid="update-intake-form-button"
      >
        {isLoadingForm ? (
          'Loading...'
        ) : isClient ? (
          <span className="block w-full text-center sm:inline sm:w-auto">{buttonText}</span>
        ) : (
          buttonText
        )}
      </button>
    </CustomTooltip>
  );

  return (
    <>
      <div
        className={classNames(
          'relative mb-2 flex flex-col gap-2 md:flex-row md:justify-between md:gap-0',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="inline-block">
            {isClient ? (
              <CompanyLogoContainer organizationDisplayName={advisor.organizationDisplayName} />
            ) : (
              <CompanyLogoContainer
                organizationDisplayName={advisor.organizationDisplayName}
                onClick={() => {
                  navigateToClientsDashboard();
                }}
              />
            )}
          </div>

          {isClient ? (
            <div className="mt-1 flex items-center justify-end md:hidden">
              <ClientUserMenu
                handleClickSignOut={handleSignOut}
                clientEmail={auth0Session?.clientUser?.email ?? ''}
                onNavigateHome={navigateToClientHome}
                showEmail={false}
              />
            </div>
          ) : null}
        </div>

        {actionButton ? (
          actionButton
        ) : isClient ? (
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <ContactAdvisorButton
                className="w-full sm:w-auto"
                requestMeetingOnClick={() => setIsRequestMeetingOpen(true)}
              />
              {updateIntakeFormButton}
            </div>

            <div className="hidden items-center justify-end md:flex">
              <ClientUserMenu
                handleClickSignOut={handleSignOut}
                clientEmail={auth0Session?.clientUser?.email ?? ''}
                onNavigateHome={navigateToClientHome}
                showEmail={false}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {updateIntakeFormButton}
            <button
              className="border-primary-100 bg-primary-50 hover:bg-primary-100 hover:text-primary-dark flex shrink-0 items-center justify-center rounded-full border p-1.5 text-gray-700"
              onClick={() => {
                navigateToClientsDashboard();
              }}
            >
              <XMarkIcon
                className="h-6 w-6 md:h-7 md:w-7"
                strokeWidth={1}
              />
            </button>
          </div>
        )}
      </div>
      <RequestAdvisorMeetingModal
        open={isRequestMeetingOpen}
        onClose={() => setIsRequestMeetingOpen(false)}
      />
    </>
  );
}
